import type { RouteObject } from 'react-router-dom';
import { RequirePermission } from '@/routes/RequirePermission';
import { LabTicketPage } from './pages/LabTicketPage';
import { OpticalJobPage } from './pages/OpticalJobPage';
import { OpticalOrderBuilderPage } from './pages/OpticalOrderBuilderPage';
import { OpticalOrderSummaryPage } from './pages/OpticalOrderSummaryPage';
import {
  OPTICAL_BUILDER_PERMISSIONS,
  OPTICAL_SUMMARY_PERMISSIONS,
  OPTICAL_TRACKING_PERMISSIONS,
} from './permissions';

/** Wraps a route in one guard per permission, outermost first: any one missing sends the user to the unauthorised page before anything is requested. */
function guarded(permissions: readonly string[], route: RouteObject): RouteObject {
  return permissions.reduceRight<RouteObject>(
    (inner, permission) => ({
      element: <RequirePermission permission={permission} />,
      children: [inner],
    }),
    route,
  );
}

/**
 * The optical order routes. The builder lives under the customer it belongs to, so the customer travels in the path and never in a query string; the summary is the order's.
 *
 * Kept whole here so the routes the app mounts are the routes the tests exercise.
 */
export const opticalOrderRoutes: RouteObject[] = [
  guarded(OPTICAL_BUILDER_PERMISSIONS, {
    path: '/customers/:customerId/optical-orders/new',
    element: <OpticalOrderBuilderPage />,
  }),
  guarded(OPTICAL_SUMMARY_PERMISSIONS, {
    path: '/sales/optical-orders/:orderId',
    element: <OpticalOrderSummaryPage />,
  }),
  guarded(OPTICAL_TRACKING_PERMISSIONS, {
    path: '/sales/optical-jobs/:jobId',
    element: <OpticalJobPage />,
  }),
];

/** The lab ticket, printed outside the application shell like a receipt; reading it needs optical.view alone. */
export const labTicketRoute: RouteObject = guarded(OPTICAL_TRACKING_PERMISSIONS, {
  path: '/print/lab-ticket/:jobId',
  element: <LabTicketPage />,
});
